/**
 * Identity of the JS bundle actually running, for request attribution.
 *
 * ## Why this exists
 *
 * GraphQL requests carried `x-device-type` and nothing about the client BUILD,
 * so a backend log line recorded what was asked and by whom but never which
 * client asked it. Two situations then have identical signatures:
 *
 *   - a live defect in the deployed bundle, and
 *   - a stale client session still running a pre-fix bundle.
 *
 * SE-8189 is what that costs. A fix deployed on every channel on 2026-07-25/26
 * and the error it prevents kept arriving at 200–767/day for seven weeks. The
 * investigation only closed by attributing errors to `user=` and noticing that
 * seven users produced them, one of them 86%, against ~4,000 operations a day
 * fleet-wide — circumstantial, and expensive. One header answers it directly.
 *
 * ## Why the JS bundle and not the binary
 *
 * The value has to identify the RUNNING JAVASCRIPT, because that is what an OTA
 * changes and what a stale session keeps. Three nearby values look like they
 * would do and do not:
 *
 *   - `Updates.runtimeVersion` — pinned to a static `"1.2.3"` in
 *     `app.config.ts`, so it is identical across every build ever shipped and
 *     carries no information at all.
 *   - the native app version — an OTA patches the JS on top of a binary without
 *     changing it, so a fully-current session can report a months-old version.
 *   - Sentry's `release` tag — set from the binary's build-time version, with
 *     `dist` null on this project, so it has exactly the same blind spot. It
 *     was mistaken for corroborating evidence during SE-8189 and retracted.
 *
 * ## Never empty
 *
 * `buildRequestHeaders` passes its result through `removeEmptyKeys`, which
 * strips `null`, `undefined` and `""`. An unresolvable id must therefore be an
 * explicit non-empty string or the header disappears — and a missing header is
 * ambiguous in exactly the way this module exists to end: it would mean either
 * "a client older than this change" or "the instrumentation broke", with no way
 * to tell. {@link UNKNOWN_BUILD_ID} is that explicit value.
 * @module config/clientBuildIdentity
 */
import * as Updates from "expo-updates";
import { Platform } from "react-native";

/**
 * Emitted when no build identity can be resolved.
 *
 * A deliberate value rather than an omission — see the module docblock. It
 * means "this client could not say", which is a different fact from "no client
 * said", and only one of them indicates a problem with this code.
 */
export const UNKNOWN_BUILD_ID = "unknown";

/** Prefix for a native launch running the JS embedded in the binary. */
const EMBEDDED_PREFIX = "embedded";

/** Prefix for a native launch running an OTA update. */
const UPDATE_PREFIX = "update";

/** Prefix for the web bundle, whose id is injected at export time. */
const WEB_PREFIX = "web";

/**
 * A trimmed non-empty string, or null.
 * @param value - Candidate value from a platform API or the environment
 * @returns The trimmed value when it carries something, otherwise null
 */
const nonEmpty = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? "").trim();
  return trimmed === "" ? null : trimmed;
};

/**
 * Identity of the running WEB bundle.
 *
 * Read from `EXPO_PUBLIC_BUILD_ID`, which the web export bakes in at build
 * time. Web deploys from a separate AWS Amplify app with no step in
 * `.github/workflows`, so until that build sets the variable this resolves to
 * {@link UNKNOWN_BUILD_ID} — honestly, and visibly, rather than by going quiet.
 *
 * Web is NOT skipped the way `getDeviceInfo` skips it. A browser tab that is
 * never reloaded never picks up new JS, which is the stale-session shape
 * SE-8189 was actually made of, so leaving web out would omit the case that
 * motivated the work.
 * @returns The web build identity
 */
const webBuildId = (): string => {
  const injected = nonEmpty(process.env.EXPO_PUBLIC_BUILD_ID);
  return injected === null ? UNKNOWN_BUILD_ID : `${WEB_PREFIX}:${injected}`;
};

/**
 * Identity of the running NATIVE bundle.
 *
 * `Updates.updateId` names the update in force and is the value that changes
 * when a JS-only fix ships — so it is the identifier in BOTH cases. What it is
 * NOT is a way to tell an OTA launch from an embedded one: it is non-null for
 * an embedded launch too, where it names the manifest created at build time.
 * Reading `updateId !== null` as "an OTA is applied" would therefore label
 * every native launch `update:` and never emit `embedded:` at all — the id
 * would still be unique per build, so attribution would work, but the prefix
 * would assert an OTA that never happened. Caught in review on SE-8286 before
 * it shipped; `Updates.isEmbeddedLaunch` is the flag that actually separates
 * them, and Expo's own guidance is to use it for exactly this question.
 *
 * The distinction is worth the extra read rather than cosmetic: an embedded
 * update is bundled into the binary and is NOT tracked in the EAS dashboard,
 * so a `update:<id>` that cannot be found there would send the next reader
 * hunting a missing OTA instead of recognising an embedded launch.
 *
 * `createdAt` is the fallback for the case neither covers — `expo-updates`
 * disabled, where there is no update id at all but the embedded manifest still
 * carries a timestamp that moves with each binary.
 *
 * Deliberately reads ONLY `expo-updates`. An earlier draft took the native
 * build number from `expo-constants` and that pulled `expo-modules-core` into
 * `ApolloClient.tsx`'s import graph, which broke eighteen existing test suites
 * at load time — a telemetry helper is not worth widening the dependency graph
 * of the module that builds every request's headers.
 * @returns The native build identity
 */
const nativeBuildId = (): string => {
  const prefix = Updates.isEmbeddedLaunch ? EMBEDDED_PREFIX : UPDATE_PREFIX;

  const updateId = nonEmpty(Updates.updateId);
  if (updateId !== null) return `${prefix}:${updateId}`;

  const createdAt = Updates.createdAt;
  return createdAt instanceof Date && !Number.isNaN(createdAt.getTime())
    ? `${prefix}:${createdAt.toISOString()}`
    : UNKNOWN_BUILD_ID;
};

/**
 * Identity of the JS bundle this client is running.
 *
 * Always a non-empty string. Never throws: it is called while assembling
 * headers for every request, so a platform API that misbehaves must degrade to
 * {@link UNKNOWN_BUILD_ID} rather than take the request down with it. An
 * observability header that can break a request is worse than no header.
 * @returns A non-empty build identity, possibly {@link UNKNOWN_BUILD_ID}
 */
export function getClientBuildId(): string {
  try {
    return Platform.OS === "web" ? webBuildId() : nativeBuildId();
  } catch {
    return UNKNOWN_BUILD_ID;
  }
}
